import { useState } from 'react';
import {
  AlertTriangle, CheckCircle2, XCircle, UserX, Clock, Calendar, ChevronRight,
  ChevronDown, Filter, MoreHorizontal, MessageCircle, ChevronLeft, Plus, Search,
  Bell, LayoutDashboard, Users, FileText, Settings, Stethoscope, RotateCcw,
  AlertCircle, MapPin, Video, X, ArrowRight
} from 'lucide-react';

// =================================================================
// Mock data: consultas passadas sem status final
// =================================================================
const PENDENTES = [
  { id: 1, paciente: 'Beatriz Mendes Oliveira', proc: 'Consulta Ginecológica', tipo: 'presencial', data: '28/04/2026', dia: 'Terça', hora: '09:00', dias_atras: 3 },
  { id: 2, paciente: 'Carla Souza Lima', proc: 'Reposição Hormonal', tipo: 'online', data: '28/04/2026', dia: 'Terça', hora: '10:30', dias_atras: 3 },
  { id: 3, paciente: 'Helena Martins Barbosa', proc: 'Retorno · Consulta', tipo: 'retorno', data: '27/04/2026', dia: 'Segunda', hora: '14:00', dias_atras: 4 },
  { id: 4, paciente: 'Mariana Dias Cardoso', proc: 'Preventivo · Papanicolau', tipo: 'presencial', data: '25/04/2026', dia: 'Sábado', hora: '11:00', dias_atras: 6 },
  { id: 5, paciente: 'Aline Rocha Pereira', proc: 'Consulta Ginecológica', tipo: 'presencial', data: '23/04/2026', dia: 'Quinta', hora: '15:30', dias_atras: 8 },
  { id: 6, paciente: 'Sofia Cardoso Lima', proc: 'Retorno', tipo: 'retorno', data: '22/04/2026', dia: 'Quarta', hora: '16:00', dias_atras: 9 },
];

const TIPO_CFG = {
  presencial: { icon: MapPin,    color: 'text-indigo-700',  bg: 'bg-indigo-50',  ring: 'ring-indigo-200',  label: 'Presencial' },
  online:     { icon: Video,     color: 'text-cyan-700',    bg: 'bg-cyan-50',    ring: 'ring-cyan-200',    label: 'Online' },
  retorno:    { icon: RotateCcw, color: 'text-rose-700',    bg: 'bg-rose-50',    ring: 'ring-rose-200',    label: 'Retorno' },
};

// =================================================================
// SHARED: Sidebar + TopBar (matching new design system)
// =================================================================
const Sidebar = ({ active = 'agenda', pendentesCount = 6 }) => {
  const items = [
    { key: 'dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { key: 'agenda', icon: Calendar, label: 'Agenda' },
    { key: 'pendencias', icon: AlertCircle, label: 'Pendências', badge: pendentesCount, badgeColor: 'amber' },
    { key: 'controle-retorno', icon: RotateCcw, label: 'Controle de Retorno' },
    { key: 'pacientes', icon: Users, label: 'Pacientes' },
    { key: 'prontuarios', icon: FileText, label: 'Prontuários' },
    { key: 'configuracoes', icon: Settings, label: 'Configurações' },
  ];
  return (
    <aside className="w-60 flex-shrink-0 bg-white border-r border-gray-200 flex flex-col">
      <div className="p-4 border-b border-gray-200 flex items-center gap-2.5">
        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center">
          <Stethoscope className="w-4 h-4 text-white" strokeWidth={2}/>
        </div>
        <div>
          <div className="text-sm font-bold text-gray-900 leading-none tracking-tight">Agenda Mais</div>
          <div className="text-[9px] tracking-[0.18em] uppercase mt-1 text-gray-500">Med</div>
        </div>
      </div>
      <nav className="flex-1 p-2 space-y-0.5">
        {items.map(it => {
          const Icon = it.icon;
          const isActive = it.key === active;
          return (
            <a key={it.key} href="#"
              className={`flex items-center gap-2.5 px-3 py-2 rounded-md text-[13px] font-medium transition-colors ${
                isActive ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-50'
              }`}>
              <Icon className="w-4 h-4" strokeWidth={1.8} />
              <span className="flex-1">{it.label}</span>
              {it.badge && (
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                  it.badgeColor === 'amber' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'
                }`}>
                  {it.badge}
                </span>
              )}
            </a>
          );
        })}
      </nav>
    </aside>
  );
};

const TopBar = ({ title, subtitle }) => (
  <header className="bg-white border-b border-gray-200 px-7 py-4 flex items-center justify-between flex-shrink-0">
    <div>
      <h1 className="text-xl font-bold text-gray-900 tracking-tight">{title}</h1>
      {subtitle && <p className="text-[12px] mt-0.5 text-gray-500">{subtitle}</p>}
    </div>
    <div className="flex items-center gap-2">
      <button className="w-8 h-8 rounded-md flex items-center justify-center relative text-gray-500">
        <Bell className="w-4 h-4" strokeWidth={1.8} />
        <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 rounded-full bg-amber-500"/>
      </button>
    </div>
  </header>
);

// =================================================================
// Quick action buttons (shared)
// =================================================================
const QuickActions = ({ size = 'md' }) => {
  const sm = size === 'sm';
  const cls = sm ? 'h-7 px-2 text-[10px]' : 'h-8 px-2.5 text-[11px]';
  return (
    <div className="flex items-center gap-1.5">
      <button className={`${cls} rounded-md font-semibold inline-flex items-center gap-1 bg-green-600 hover:bg-green-700 text-white shadow-sm`}>
        <CheckCircle2 className={sm ? 'w-3 h-3' : 'w-3.5 h-3.5'} strokeWidth={2.2}/>
        Realizada
      </button>
      <button className={`${cls} rounded-md font-semibold inline-flex items-center gap-1 bg-amber-500 hover:bg-amber-600 text-white shadow-sm`}>
        <UserX className={sm ? 'w-3 h-3' : 'w-3.5 h-3.5'} strokeWidth={2.2}/>
        Falta
      </button>
      <button className={`${cls} rounded-md font-semibold inline-flex items-center gap-1 bg-white border border-red-300 text-red-600 hover:bg-red-50`}>
        <XCircle className={sm ? 'w-3 h-3' : 'w-3.5 h-3.5'} strokeWidth={2.2}/>
        Cancelar
      </button>
    </div>
  );
};

const TipoTag = ({ tipo }) => {
  const cfg = TIPO_CFG[tipo];
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider ${cfg.bg} ${cfg.color}`}>
      <Icon className="w-2.5 h-2.5" strokeWidth={2.2}/>
      {cfg.label}
    </span>
  );
};

const Avatar = ({ name, hue = 'blue' }) => {
  const initials = name.split(' ').filter(Boolean).slice(0, 2).map(s => s[0]).join('').toUpperCase();
  const map = {
    blue: 'bg-blue-100 text-blue-700',
    amber: 'bg-amber-100 text-amber-700',
    rose: 'bg-rose-100 text-rose-700',
  };
  return (
    <div className={`w-9 h-9 rounded-full flex items-center justify-center font-semibold text-xs flex-shrink-0 ${map[hue]}`}>
      {initials}
    </div>
  );
};

// =================================================================
// OPÇÃO A — Banner alerta na Agenda + drawer expansível
// =================================================================
const OptionA = () => {
  const [expanded, setExpanded] = useState(true);

  return (
    <div className="flex h-full bg-gray-50 font-geist">
      <Sidebar active="agenda"/>
      <div className="flex-1 flex flex-col overflow-hidden">
        <TopBar title="Agenda" subtitle="Sexta · 1 de maio, 2026"/>

        <main className="flex-1 overflow-y-auto p-6">
          {/* === BANNER ALERTA === */}
          <div className="mb-5 rounded-xl border-2 border-amber-300 bg-gradient-to-r from-amber-50 to-orange-50 overflow-hidden shadow-sm">
            <button
              onClick={() => setExpanded(!expanded)}
              className="w-full px-5 py-3.5 flex items-center justify-between hover:bg-amber-100/40 transition-colors">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-amber-500 flex items-center justify-center flex-shrink-0 shadow-sm ring-4 ring-amber-200/60">
                  <AlertTriangle className="w-5 h-5 text-white" strokeWidth={2.2}/>
                </div>
                <div className="text-left">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold text-amber-900">6 consultas passadas sem status</span>
                    <span className="px-2 py-0.5 rounded-full bg-amber-200 text-amber-900 text-[10px] font-bold">PENDENTES</span>
                  </div>
                  <div className="text-[12px] text-amber-800 mt-0.5">Defina como <strong>Realizada</strong>, <strong>Falta</strong> ou <strong>Cancelada</strong> para manter o controle de retorno preciso.</div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-[11px] font-semibold text-amber-700">{expanded ? 'Recolher' : 'Ver consultas'}</span>
                {expanded ? <ChevronDown className="w-4 h-4 text-amber-700"/> : <ChevronRight className="w-4 h-4 text-amber-700"/>}
              </div>
            </button>

            {expanded && (
              <div className="border-t border-amber-200 bg-white">
                {PENDENTES.slice(0, 4).map((p, i) => (
                  <div key={p.id} className={`px-5 py-3 flex items-center gap-4 hover:bg-amber-50/50 transition-colors ${i < 3 ? 'border-b border-gray-100' : ''}`}>
                    <Avatar name={p.paciente} hue="amber"/>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-[13px] font-bold text-gray-900 truncate">{p.paciente}</span>
                        <TipoTag tipo={p.tipo}/>
                      </div>
                      <div className="text-[11px] text-gray-500 flex items-center gap-2">
                        <span>{p.proc}</span>
                        <span className="w-1 h-1 rounded-full bg-gray-300"/>
                        <span className="font-mono">{p.data} · {p.hora}</span>
                        <span className="px-1.5 py-0 rounded text-[9px] font-bold bg-amber-100 text-amber-800">há {p.dias_atras} dias</span>
                      </div>
                    </div>
                    <QuickActions size="sm"/>
                  </div>
                ))}
                <div className="px-5 py-2.5 bg-amber-50/60 border-t border-amber-100 flex items-center justify-between">
                  <span className="text-[11px] text-amber-800">Mostrando 4 de 6</span>
                  <button className="text-[11px] font-semibold text-amber-900 hover:underline inline-flex items-center gap-1">
                    Ver todas e processar em lote <ArrowRight className="w-3 h-3"/>
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Mock da agenda */}
          <div className="rounded-xl border border-gray-200 bg-white p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-bold text-gray-900">Hoje · 1 de maio</h2>
              <button className="px-3 py-1.5 rounded-md text-[12px] font-semibold bg-blue-600 hover:bg-blue-700 text-white shadow-sm inline-flex items-center gap-1.5">
                <Plus className="w-3.5 h-3.5" strokeWidth={2.2}/>
                Nova Consulta
              </button>
            </div>
            <div className="space-y-2">
              {['08:00 · Bloqueado · Almoço', '09:00 · Ana Costa · Confirmada', '14:00 · Sandra Silva · Não confirmada'].map((s, i) => (
                <div key={i} className="px-4 py-3 rounded-md bg-gray-50 text-[12px] text-gray-700">{s}</div>
              ))}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

// =================================================================
// OPÇÃO B — Página dedicada "Pendências" no menu
// =================================================================
const OptionB = () => {
  const [filter, setFilter] = useState('todos');
  const [agruparPor, setAgruparPor] = useState('data');

  return (
    <div className="flex h-full bg-gray-50 font-geist">
      <Sidebar active="pendencias"/>
      <div className="flex-1 flex flex-col overflow-hidden">
        <TopBar title="Pendências de Status"
          subtitle="Consultas passadas que precisam de Realizada · Falta · Cancelada"/>

        <main className="flex-1 overflow-y-auto p-6">
          {/* KPIs */}
          <div className="grid grid-cols-4 gap-3 mb-5">
            {[
              { l: 'Total pendentes', v: '6', accent: 'amber', icon: AlertCircle },
              { l: 'Há mais de 7 dias', v: '2', accent: 'red', icon: Clock },
              { l: 'Esta semana', v: '4', accent: 'blue', icon: Calendar },
              { l: 'Maior atraso', v: '9 dias', accent: 'red', icon: AlertTriangle, sub: 'Sofia Cardoso · 22/04' },
            ].map((k, i) => {
              const Icon = k.icon;
              const colors = {
                amber: { bg: 'bg-amber-100', text: 'text-amber-700', value: 'text-amber-700' },
                red:   { bg: 'bg-red-100',   text: 'text-red-700',   value: 'text-red-700' },
                blue:  { bg: 'bg-blue-100',  text: 'text-blue-700',  value: 'text-gray-900' },
              }[k.accent];
              return (
                <div key={i} className="rounded-xl border border-gray-200 bg-white p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-[10px] uppercase tracking-[0.1em] font-semibold text-gray-500">{k.l}</div>
                    <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${colors.bg} ${colors.text}`}>
                      <Icon className="w-3.5 h-3.5" strokeWidth={2}/>
                    </div>
                  </div>
                  <div className={`font-mono text-[26px] font-bold tabular-nums leading-none ${colors.value}`}>{k.v}</div>
                  {k.sub && <div className="text-[10px] text-gray-500 mt-2">{k.sub}</div>}
                </div>
              );
            })}
          </div>

          {/* Toolbar */}
          <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-semibold text-gray-500 inline-flex items-center gap-1.5">
                <Filter className="w-3.5 h-3.5"/>Filtrar:
              </span>
              {[
                { k: 'todos', l: 'Todos', count: 6 },
                { k: 'recentes', l: 'Últimos 7 dias', count: 4 },
                { k: 'antigas', l: '+ 7 dias', count: 2, urgent: true },
                { k: 'retorno', l: 'Retornos', count: 2 },
              ].map(f => (
                <button key={f.k} onClick={() => setFilter(f.k)}
                  className={`px-3 py-1.5 rounded-full text-[11px] font-semibold inline-flex items-center gap-1.5 border transition-colors ${
                    filter === f.k
                      ? 'bg-blue-600 border-blue-600 text-white'
                      : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}>
                  {f.l}
                  <span className={`px-1.5 py-0 rounded text-[10px] font-mono ${
                    filter === f.k ? 'bg-white/25' : f.urgent ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'
                  }`}>{f.count}</span>
                </button>
              ))}
            </div>
            <div className="flex items-center gap-1">
              <span className="text-[11px] text-gray-500">Agrupar por:</span>
              <div className="inline-flex p-0.5 rounded-md border border-gray-200 bg-gray-50">
                {['data', 'paciente'].map(g => (
                  <button key={g} onClick={() => setAgruparPor(g)}
                    className={`px-3 py-1 text-[11px] font-medium rounded capitalize ${
                      agruparPor === g ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'
                    }`}>{g}</button>
                ))}
              </div>
            </div>
          </div>

          {/* Lista agrupada por data */}
          <div className="space-y-3">
            {['28/04/2026 · Terça', '27/04/2026 · Segunda', '25/04/2026 · Sábado'].map((label, gi) => (
              <div key={gi} className="rounded-xl border border-gray-200 bg-white overflow-hidden">
                <div className="px-5 py-2.5 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-gray-700">{label}</span>
                    <span className="text-[11px] text-gray-500">· {gi === 0 ? 2 : gi === 1 ? 1 : 1} consulta{gi === 0 ? 's' : ''}</span>
                  </div>
                  {gi === 2 && <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-100 text-red-700">há 6 dias</span>}
                </div>
                {PENDENTES.filter(p => label.startsWith(p.data)).map((p, i, arr) => (
                  <div key={p.id} className={`px-5 py-3 flex items-center gap-4 hover:bg-gray-50/60 transition-colors ${i < arr.length - 1 ? 'border-b border-gray-100' : ''}`}>
                    <input type="checkbox" className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"/>
                    <div className="font-mono text-[12px] tabular-nums text-gray-700 w-12">{p.hora}</div>
                    <Avatar name={p.paciente}/>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-[13px] font-bold text-gray-900 truncate">{p.paciente}</span>
                        <TipoTag tipo={p.tipo}/>
                      </div>
                      <div className="text-[11px] text-gray-500">{p.proc}</div>
                    </div>
                    <QuickActions/>
                  </div>
                ))}
              </div>
            ))}
          </div>

          {/* Bulk action bar */}
          <div className="mt-5 rounded-xl border border-blue-200 bg-blue-50 px-5 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2 text-[12px] text-blue-900">
              <CheckCircle2 className="w-4 h-4"/>
              Selecione consultas acima para aplicar uma ação em lote
            </div>
            <div className="flex items-center gap-2 opacity-50">
              <button className="px-3 py-1.5 rounded-md text-[11px] font-semibold bg-white border border-gray-300 text-gray-500" disabled>
                Marcar como Realizadas em lote
              </button>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

// =================================================================
// OPÇÃO C — Card flutuante / Triagem rápida modal
// =================================================================
const OptionC = () => {
  const [currentIdx, setCurrentIdx] = useState(0);
  const current = PENDENTES[currentIdx];
  const cfg = TIPO_CFG[current.tipo];
  const TipoIcon = cfg.icon;

  return (
    <div className="flex h-full bg-gray-50 font-geist">
      <Sidebar active="agenda"/>
      <div className="flex-1 flex flex-col overflow-hidden">
        <TopBar title="Agenda" subtitle="Sexta · 1 de maio, 2026"/>

        <main className="flex-1 overflow-y-auto p-6 relative">
          {/* Mock da agenda atrás (esmaecida) */}
          <div className="opacity-30 pointer-events-none">
            <div className="rounded-xl border border-gray-200 bg-white p-5">
              <h2 className="text-base font-bold text-gray-900 mb-3">Hoje · 1 de maio</h2>
              <div className="space-y-2">
                {['08:00 · Ana Costa', '10:30 · Sandra Silva', '14:00 · Renata Costa'].map((s, i) => (
                  <div key={i} className="px-4 py-3 rounded-md bg-gray-50 text-[12px]">{s}</div>
                ))}
              </div>
            </div>
          </div>

          {/* Floating triage card */}
          <div className="absolute inset-0 flex items-center justify-center p-6 pointer-events-none">
            <div className="w-full max-w-2xl pointer-events-auto">
              <div className="rounded-2xl bg-white shadow-2xl border-2 border-amber-300 overflow-hidden">
                {/* Header */}
                <div className="px-6 py-4 bg-gradient-to-r from-amber-50 to-orange-50 border-b border-amber-200 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-amber-500 flex items-center justify-center shadow-sm ring-4 ring-amber-200/60">
                      <AlertTriangle className="w-5 h-5 text-white" strokeWidth={2.2}/>
                    </div>
                    <div>
                      <h2 className="text-base font-bold text-amber-900">Triagem de Pendências</h2>
                      <p className="text-[12px] text-amber-800">Defina o status de consultas passadas</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="px-2.5 py-0.5 rounded-full bg-amber-200 text-amber-900 text-[11px] font-bold">
                      {currentIdx + 1} de {PENDENTES.length}
                    </span>
                    <button className="w-8 h-8 rounded-md hover:bg-amber-200/50 flex items-center justify-center text-amber-700">
                      <X className="w-4 h-4"/>
                    </button>
                  </div>
                </div>

                {/* Progress bar */}
                <div className="h-1 bg-amber-100">
                  <div className="h-full bg-amber-500 transition-all" style={{ width: `${((currentIdx) / PENDENTES.length) * 100}%` }}/>
                </div>

                {/* Body */}
                <div className="p-6">
                  <div className="flex items-center gap-4 mb-5">
                    <div className="w-16 h-16 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-bold text-lg flex-shrink-0">
                      {current.paciente.split(' ').slice(0, 2).map(s => s[0]).join('')}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-bold text-gray-900 mb-1">{current.paciente}</h3>
                      <div className="flex items-center gap-2 text-[12px] text-gray-600 flex-wrap">
                        <TipoTag tipo={current.tipo}/>
                        <span>·</span>
                        <span>{current.proc}</span>
                      </div>
                    </div>
                  </div>

                  {/* Date info */}
                  <div className="grid grid-cols-3 gap-3 mb-6">
                    <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                      <div className="text-[10px] uppercase tracking-wider font-bold text-gray-500 mb-1">Data</div>
                      <div className="font-mono text-sm font-bold text-gray-900">{current.data}</div>
                      <div className="text-[10px] text-gray-500">{current.dia} · {current.hora}</div>
                    </div>
                    <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                      <div className="text-[10px] uppercase tracking-wider font-bold text-amber-700 mb-1">Atraso</div>
                      <div className="font-mono text-sm font-bold text-amber-900">{current.dias_atras} dias</div>
                      <div className="text-[10px] text-amber-700">sem definição</div>
                    </div>
                    <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                      <div className="text-[10px] uppercase tracking-wider font-bold text-gray-500 mb-1">Histórico</div>
                      <div className="text-[12px] font-semibold text-gray-900">5 consultas</div>
                      <button className="text-[10px] text-blue-600 hover:underline">Ver paciente →</button>
                    </div>
                  </div>

                  {/* Big action buttons */}
                  <div className="text-[11px] uppercase tracking-wider font-bold text-gray-500 mb-2">A consulta foi:</div>
                  <div className="grid grid-cols-3 gap-2 mb-4">
                    <button className="py-3 rounded-lg bg-green-600 hover:bg-green-700 text-white shadow-sm transition-all hover:shadow-md flex flex-col items-center gap-1">
                      <CheckCircle2 className="w-5 h-5" strokeWidth={2}/>
                      <span className="text-[12px] font-bold">Realizada</span>
                      <span className="text-[9px] opacity-80">Tecla 1</span>
                    </button>
                    <button className="py-3 rounded-lg bg-amber-500 hover:bg-amber-600 text-white shadow-sm transition-all hover:shadow-md flex flex-col items-center gap-1">
                      <UserX className="w-5 h-5" strokeWidth={2}/>
                      <span className="text-[12px] font-bold">Falta</span>
                      <span className="text-[9px] opacity-80">Tecla 2</span>
                    </button>
                    <button className="py-3 rounded-lg bg-white border border-red-300 hover:bg-red-50 text-red-600 shadow-sm transition-all hover:shadow-md flex flex-col items-center gap-1">
                      <XCircle className="w-5 h-5" strokeWidth={2}/>
                      <span className="text-[12px] font-bold">Cancelada</span>
                      <span className="text-[9px] opacity-80">Tecla 3</span>
                    </button>
                  </div>

                  <div className="flex items-center justify-between text-[11px]">
                    <button className="text-gray-500 hover:text-gray-700 inline-flex items-center gap-1">
                      <Clock className="w-3 h-3"/>Decidir depois
                    </button>
                    <button className="text-blue-600 hover:underline font-semibold inline-flex items-center gap-1">
                      Pular esta <ChevronRight className="w-3 h-3"/>
                    </button>
                  </div>
                </div>

                {/* Footer */}
                <div className="px-6 py-3 bg-gray-50 border-t border-gray-200 flex items-center justify-between text-[11px]">
                  <button onClick={() => setCurrentIdx(Math.max(0, currentIdx - 1))}
                    disabled={currentIdx === 0}
                    className="inline-flex items-center gap-1 text-gray-700 hover:text-gray-900 disabled:opacity-40 disabled:cursor-not-allowed">
                    <ChevronLeft className="w-3.5 h-3.5"/> Anterior
                  </button>
                  <span className="text-gray-500">Use o teclado para triagem rápida</span>
                  <button onClick={() => setCurrentIdx(Math.min(PENDENTES.length - 1, currentIdx + 1))}
                    disabled={currentIdx === PENDENTES.length - 1}
                    className="inline-flex items-center gap-1 text-gray-700 hover:text-gray-900 disabled:opacity-40 disabled:cursor-not-allowed">
                    Próxima <ChevronRight className="w-3.5 h-3.5"/>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

// =================================================================
// PAGE
// =================================================================
export default function PendenciasMockupsPage() {
  const [option, setOption] = useState('A');

  const Screen = { A: OptionA, B: OptionB, C: OptionC }[option];

  return (
    <div className="min-h-screen bg-slate-100 p-6">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Geist:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500;600&display=swap');
        .font-geist { font-family: 'Geist', system-ui, -apple-system, sans-serif; }
        .font-mono { font-family: 'JetBrains Mono', ui-monospace, monospace; }
      `}</style>

      <div className="max-w-[1500px] mx-auto">
        <header className="mb-5">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[10px] uppercase tracking-[0.15em] font-bold text-gray-500">Mockups · Pendências de Status</span>
          </div>
          <h1 className="font-geist text-3xl font-bold text-gray-900 tracking-tight">
            Como exibir consultas passadas <span className="text-amber-600">sem status</span>?
          </h1>
          <p className="text-sm mt-1 max-w-3xl text-gray-600">
            3 abordagens para que a recepcionista atribua <strong>Realizada</strong>, <strong>Falta</strong> ou <strong>Cancelada</strong>
            em consultas que já passaram da data — protegendo a precisão do controle de retorno.
          </p>
        </header>

        {/* Option tabs */}
        <div className="grid grid-cols-3 gap-3 mb-5">
          {[
            {
              k: 'A',
              tag: 'Banner',
              titulo: 'Banner persistente na Agenda',
              desc: 'Alerta âmbar fixo no topo da Agenda, expansível com lista inline + ações rápidas em cada linha. Sempre visível durante o trabalho diário.',
              prós: 'Zero cliques · sempre visível · contexto natural',
              contras: 'Ocupa espaço · pode ser ignorado se sempre presente',
            },
            {
              k: 'B',
              tag: 'Página',
              titulo: 'Página dedicada "Pendências"',
              desc: 'Item próprio no menu lateral com badge âmbar mostrando contagem. Tela completa com KPIs, filtros, agrupamento por data/paciente e seleção em lote.',
              prós: 'Foco total · processamento em lote · estatísticas',
              contras: 'Requer navegar até a página · menos imediato',
            },
            {
              k: 'C',
              tag: 'Modal',
              titulo: 'Modal de triagem rápida',
              desc: 'Dispara automaticamente ao abrir a Agenda se houver pendências. Triagem foco-único: 1 paciente por vez, atalhos de teclado (1=Realizada, 2=Falta, 3=Cancelar).',
              prós: 'Forçar ação · velocidade máxima · keyboard-first',
              contras: 'Interruptivo · pode irritar se for diário',
            },
          ].map(opt => {
            const active = option === opt.k;
            return (
              <button key={opt.k} onClick={() => setOption(opt.k)}
                className={`p-4 text-left rounded-xl border-2 transition-all bg-white ${
                  active ? 'border-blue-600 shadow-md' : 'border-gray-200 hover:border-gray-300'
                }`}>
                <div className="flex items-center justify-between mb-2">
                  <span className={`text-[10px] uppercase tracking-[0.15em] font-bold ${active ? 'text-blue-600' : 'text-gray-400'}`}>
                    Opção {opt.k} · {opt.tag}
                  </span>
                  {active && (
                    <span className="px-2 py-0.5 rounded-full bg-blue-600 text-white text-[10px] font-bold">SELECIONADA</span>
                  )}
                </div>
                <div className="font-bold text-[14px] text-gray-900 mb-1">{opt.titulo}</div>
                <div className="text-[11px] text-gray-600 leading-snug mb-3">{opt.desc}</div>
                <div className="space-y-1">
                  <div className="text-[10px]"><span className="text-green-700 font-bold">+ </span><span className="text-gray-700">{opt.prós}</span></div>
                  <div className="text-[10px]"><span className="text-red-700 font-bold">− </span><span className="text-gray-700">{opt.contras}</span></div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Frame */}
        <div className="rounded-2xl overflow-hidden shadow-2xl border-8 border-slate-800 bg-slate-100" style={{ height: 760 }}>
          <Screen/>
        </div>

        <p className="text-xs mt-4 text-center text-gray-400">
          Use o design system padronizado (azul-600 primário · verde-600 sucesso · âmbar-500 atenção · vermelho destrutivo)
        </p>
      </div>
    </div>
  );
}
